import { ComputedRef, computed } from 'vue';
import type { FormItemRule } from 'element-plus';
import { storeToRefs } from 'pinia';
import type { WeekDayEnum } from '@/models/dictionary/day-of-week.model';
import type { UnavailableHoursTimeModel } from '@/models/ps-management/availability-hours.model';
import { usePaymentMethodsAvailabilityHoursStore } from '@/store/ps-management/availability-hours.store';

type AvailabilityHoursRule = Record<string, FormItemRule[]>;

type FieldValidator = NonNullable<FormItemRule['validator']>;

type AvailabilityHoursRulesResult = {
  availabilityHoursFormRules: ComputedRef<AvailabilityHoursRule>;
}

export const useAvailabilityHoursRules = (): AvailabilityHoursRulesResult => {
  const { availabilityHoursFormData } = storeToRefs(usePaymentMethodsAvailabilityHoursStore());

  const validateNoDuplicates: FieldValidator = (_rule, value, callback) => {
    const list = Array.isArray(value) ? (value as WeekDayEnum[]) : [];

    if (!list.length) {
      return callback(new Error('Please select at least one day'));
    }

    const set = new Set(list);
    if (set.size !== list.length) {
      return callback(new Error('Duplicate days not allowed'));
    }

    callback();
  };

  const validateTimeRange: FieldValidator = (_rule, value, callback) => {
    const time = value as UnavailableHoursTimeModel | undefined;

    if (!time?.from || !time?.to) {
      return callback(new Error('Please select time range'));
    }

    if (time.from >= time.to) {
      return callback(new Error('Start time must be earlier than end time'));
    }

    callback();
  };

  const availabilityHoursFormRules = computed<AvailabilityHoursRule>(() => {
    const list = availabilityHoursFormData.value?.unavailableHoursConfig ?? [];

    return list.reduce<AvailabilityHoursRule>((result, _item, index) => {
      const dayListKey = `unavailableHoursConfig.${index}.dayList`;
      const timeKey = `unavailableHoursConfig.${index}.time`;

      return {
        ...result,
        [dayListKey]: [
          { required: true, type: 'array', message: 'Must not be empty', trigger: 'change' },
          { validator: validateNoDuplicates, trigger: 'change' },
        ],
        [timeKey]: [
          { required: true, type: 'object', message: 'Must not be empty', trigger: 'change' },
          { validator: validateTimeRange, trigger: 'change' },
        ],
      };
    }, {});
  });

  return { availabilityHoursFormRules };
};
